// angular
import {Injectable} from '@angular/core';
// rxjs
import {BehaviorSubject, Observable} from 'rxjs';
// models
import {BillingAddress} from '../models/payment-item.model';
import {CreateSubscriptionData, DeliveryAddress, DeliveryType, FulfillmentType, OrderData, SubscriptionPhase} from '../models/order.model';
import {Customer, LoyaltyRewardTier} from '../models/customer.model';

export enum StatePayment {
  INIT = 'init',
  SEND = 'send',
  WALLET_PROCESS = 'wallet',
  SUCCESS = 'success',
  SUBSCRIBED = 'SUBSCRIBED',
  FAILED = 'failed',
  TIMEOVER = 'timeover'
}

export enum VerifyProcess {
  INIT = 'INIT',
  VERIFYING = 'VERIFYING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export enum ApplyPromoCodeProcess {
  INIT = 'INIT',
  SEND = 'SEND',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

@Injectable()
export class PaymentStore {

  private _statePayment$ = new BehaviorSubject<StatePayment>(StatePayment.INIT);
  private _verifyCustomerState$ = new BehaviorSubject<VerifyProcess>(VerifyProcess.INIT);
  private _applyPromoCodeState$ = new BehaviorSubject<ApplyPromoCodeProcess>(ApplyPromoCodeProcess.INIT);
  private _verificationCustomer$ = new BehaviorSubject<boolean>(false);
  private _fulfilmentType$ = new BehaviorSubject<FulfillmentType>(null);
  private _deliveryType$ = new BehaviorSubject<DeliveryType>(null);
  private _createTabChecked = new BehaviorSubject<boolean>(false);
  private _useLoyaltyChecked = new BehaviorSubject<boolean>(false);
  private _loyaltyEnabled = new BehaviorSubject<boolean>(false);
  private _savedCardId = new BehaviorSubject<string>(null);
  private _transactionId = new BehaviorSubject<string>(null);
  private _phoneNumber = new BehaviorSubject<string>(null);
  private _givenName = new BehaviorSubject<string>(null);
  private _familyName = new BehaviorSubject<string>(null);
  private _email = new BehaviorSubject<string>(null);
  private _billingAddress = new BehaviorSubject<BillingAddress>(null);
  private _tableNumber = new BehaviorSubject<string>(null);
  private _deliveryAddress = new BehaviorSubject<DeliveryAddress>(new DeliveryAddress('', '', '', '', '', ''));
  private _saveDeliveryAddress = new BehaviorSubject<boolean>(false);
  private _customer = new BehaviorSubject<Customer>(new Customer());
  private _isWaitingUpdate = new BehaviorSubject<boolean>(false);
  private _isWalletPayment = new BehaviorSubject<boolean>(false);
  private _isLinkTableNumber = new BehaviorSubject<boolean>(false);
  private _isGroupOrder = new BehaviorSubject<boolean>(false);
  private _orderData = new BehaviorSubject<OrderData>(null);
  private _createSubscriptionData = new BehaviorSubject<CreateSubscriptionData>(null);
  private _selectedLoyaltyReward = new BehaviorSubject<LoyaltyRewardTier>(null);

  public get createTabChecked$(): BehaviorSubject<boolean> {
    return this._createTabChecked;
  }

  public get createTabChecked(): boolean {
    return this._createTabChecked.getValue();
  }

  public set createTabChecked(value: boolean) {
    this._createTabChecked.next(value);
  }

  public get useLoyaltyChecked(): boolean {
    return this._useLoyaltyChecked.getValue();
  }

  public set useLoyaltyChecked(value: boolean) {
    this._useLoyaltyChecked.next(value);
  }

  public get loyaltyEnabled(): boolean {
    return this._loyaltyEnabled.getValue();
  }

  public set loyaltyEnabled(value: boolean) {
    this._loyaltyEnabled.next(value);
  }

  public get selectedLoyaltyReward(): LoyaltyRewardTier {
    return this._selectedLoyaltyReward.getValue();
  }

  public set selectedLoyaltyReward(value: LoyaltyRewardTier) {
    this._selectedLoyaltyReward.next(value);
  }

  public get statePayment$(): Observable<StatePayment> {
    return this._statePayment$.asObservable();
  }

  public get statePayment(): StatePayment {
    return this._statePayment$.getValue();
  }

  public setStatePayment(value: StatePayment) {
    this._statePayment$.next(value);
  }

  public get applyPromoCodeState$(): Observable<ApplyPromoCodeProcess> {
    return this._applyPromoCodeState$.asObservable();
  }

  public get applyPromoCodeState(): ApplyPromoCodeProcess {
    return this._applyPromoCodeState$.getValue();
  }

  public set applyPromoCodeState(value: ApplyPromoCodeProcess) {
    this._applyPromoCodeState$.next(value);
  }

  public get verifyCustomerState$(): Observable<VerifyProcess> {
    return this._verifyCustomerState$.asObservable();
  }

  public set verifyCustomerState(value: VerifyProcess) {
    this._verifyCustomerState$.next(value);
  }

  public get isWalletPayment(): boolean {
    return this._isWalletPayment.getValue();
  }

  public set isWalletPayment(value: boolean) {
    this._isWalletPayment.next(value);
  }

  public get verificationCustomer$(): Observable<boolean> {
    return this._verificationCustomer$.asObservable();
  }

  public get verificationCustomer(): boolean {
    return this._verificationCustomer$.getValue();
  }

  public set verificationCustomer(value: boolean) {
    this._verificationCustomer$.next(value);
  }

  public get isWaitingUpdate(): boolean {
    return this._isWaitingUpdate.getValue();
  }

  public set isWaitingUpdate(value: boolean) {
    this._isWaitingUpdate.next(value);
  }

  public get fulfilmentType$(): BehaviorSubject<FulfillmentType> {
    return this._fulfilmentType$;
  }

  public get fulfilmentType(): FulfillmentType {
    return this._fulfilmentType$.value;
  }

  public setFulfilmentType(fulfilmentType: FulfillmentType): void {
    this._fulfilmentType$.next(fulfilmentType);
  }

  public get deliveryType(): DeliveryType {
    return this._deliveryType$.value;
  }

  public setDeliveryType(deliveryType: DeliveryType): void {
    this._deliveryType$.next(deliveryType);
  }

  public get savedCardId(): string {
    return this._savedCardId.value;
  }

  public set savedCardId(savedCardId: string) {
    this._savedCardId.next(savedCardId);
  }

  public get transactionId(): string {
    return this._transactionId.value;
  }

  public set transactionId(value: string) {
    this._transactionId.next(value);
  }

  public get phoneNumber(): string {
    return this._phoneNumber.value;
  }

  public setPhoneNumber(phoneNumber: string): void {
    this._phoneNumber.next(phoneNumber);
  }

  public get givenName(): string {
    return this._givenName.value;
  }

  public setGivenName(givenName: string): void {
    this._givenName.next(givenName);
  }

  public get familyName(): string {
    return this._familyName.value;
  }

  public setFamilyName(familyName: string): void {
    this._familyName.next(familyName);
  }

  public get email(): string {
    return this._email.value;
  }

  public setEmail(email: string): void {
    this._email.next(email);
  }

  public get billingAddress(): BillingAddress {
    return this._billingAddress.value;
  }

  public setBillingAddress(value: BillingAddress) {
    this._billingAddress.next(value);
  }

  public get tableNumber(): string {
    return this._tableNumber.value;
  }

  public set tableNumber(tableNumber: string) {
    if (tableNumber == null) {
      return;
    }
    this._tableNumber.next(tableNumber);
  }

  public get isLinkTableNumber(): boolean {
    return this._isLinkTableNumber.value;
  }

  public set isLinkTableNumber(tableNumber: boolean) {
    this._isLinkTableNumber.next(tableNumber);
  }

  public get isGroupOrder(): boolean {
    return this._isGroupOrder.value;
  }

  public set isGroupOrder(isGroupOrder: boolean) {
    this._isGroupOrder.next(isGroupOrder);
  }

  public get deliveryAddress(): DeliveryAddress {
    return this._deliveryAddress.value;
  }

  public set deliveryAddress(deliveryAddress: DeliveryAddress) {
    this._deliveryAddress.next(deliveryAddress);
  }

  public get saveDeliveryAddress(): boolean {
    return this._saveDeliveryAddress.value;
  }

  public set saveDeliveryAddress(saveDeliveryAddress: boolean) {
    this._saveDeliveryAddress.next(saveDeliveryAddress);
  }

  public get customer(): Customer {
    return this._customer.getValue();
  }

  public get customer$(): Observable<Customer> {
    return this._customer.asObservable();
  }

  public set customer(value: Customer) {
    this._customer.next(value);
  }

  public get orderData(): OrderData {
    return this._orderData.getValue();
  }

  public set orderData(value: OrderData) {
    this._orderData.next(value);
  }

  public get createSubscriptionData(): CreateSubscriptionData {
    return this._createSubscriptionData.getValue();
  }

  public set createSubscriptionData(value: CreateSubscriptionData) {
    this._createSubscriptionData.next(value);
  }

  public resetCustomerData(): void {
    this.customer = new Customer();
    this.verificationCustomer = false;
    this.setBillingAddress(null);
    this.setEmail('');
    this.setFamilyName('');
    this.setGivenName('');
    this.setPhoneNumber('');
  }
}
